import { useEffect, useMemo, useState } from 'react';
import { format, endOfYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { SttFullDialog, SttTabs } from '../../../sporttia/all';
import BonoDetails from './BonoDetails';
import BonoBoletos from './BonoBoletos';
import BonoDevices from './BonoDevices';
import { useCrud } from '../../../lib/hooks';

// Tab keys for URL navigation
const TAB_KEYS = ['detail', 'users', 'devices'] as const;
type TabKey = (typeof TAB_KEYS)[number];

type BonoChipType = 'COMPLETE' | 'SINGLE';

type MandatoryGroup = {
    id: number;
    name: string;
} | null;

type BonoData = {
    id: number | 'create';
    name: string;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: number[];
    type: BonoChipType;
    expiration: string | null;
    amount: number;
    duration: number;
    purchaseOnline: boolean;
    purchasePFs: string[];
    transferable: boolean;
    mandatoryGroup: MandatoryGroup;
    price: number;
    trash?: boolean;
};

type BonoResponse = {
    bono: BonoData;
};

type Props = {
    id?: number | 'create' | null;
    trash?: boolean;
    onChange?: (bono: BonoData) => void;
    onClose?: () => void;
    disableUrlPath?: boolean;
};

export default function Bono({
    id: propId,
    trash,
    onChange,
    onClose,
    disableUrlPath,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const { id: urlId, tab: urlTab } = useParams<{
        id: string;
        tab?: string;
    }>();
    const navigate = useNavigate();
    const [bono, setBono, , Get] = useCrud<BonoData>();
    const [loading, setLoading] = useState(false);

    // Use URL param if no prop id provided (standalone page mode)
    const id = propId !== undefined ? propId : urlId ? Number(urlId) : null;
    const isStandalonePage = propId === undefined && urlId !== undefined;

    // Determine visible tab keys based on bono state
    const visibleTabKeys = useMemo(() => {
        if (bono?.id === 'create') {
            return ['detail'] as TabKey[];
        }
        return [...TAB_KEYS];
    }, [bono?.id]);

    // Calculate initial tab from URL
    const initialTab = useMemo(() => {
        if (urlTab) {
            const index = visibleTabKeys.indexOf(urlTab as TabKey);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [urlTab, visibleTabKeys]);

    // Handle tab change - update URL in standalone mode
    const handleTabChange = (tabIndex: number) => {
        if (isStandalonePage && id) {
            const tabKey = visibleTabKeys[tabIndex];
            if (tabKey) {
                navigate(`/bonos/${id}/${tabKey}`, { replace: true });
            }
        }
    };

    // Handle close - navigate back if standalone page
    const handleClose = () => {
        if (isStandalonePage) {
            navigate('/bonos');
        } else {
            onClose?.();
        }
    };

    useEffect(() => {
        if (typeof id === 'number') {
            loadBono();
        } else if (id === 'create') {
            setBono({
                id: 'create',
                name: '',
                dateini: format(new Date(), 'yyyy-MM-dd'),
                dateend: format(endOfYear(new Date()), 'yyyy-MM-dd'),
                timeini: '06:00',
                timeend: '23:30',
                weekdays: [0, 1, 2, 3, 4, 5, 6],
                type: 'COMPLETE',
                expiration: '0000-00-00 00:00:00',
                amount: 10,
                duration: 60,
                purchaseOnline: true,
                purchasePFs: [],
                transferable: false,
                mandatoryGroup: null,
                price: 0,
            });
        } else {
            setBono(undefined as unknown as BonoData);
        }
    }, [id, trash]);

    function loadBono(): void {
        setLoading(true);
        Get(`/bonos/${id}`, { trash: trash ? true : undefined })
            .then((response) => {
                const { bono: bonoData } = response as BonoResponse;
                if (bonoData.expiration === null) {
                    bonoData.expiration = '0000-00-00 00:00:00';
                }
                setBono(bonoData);
            })
            .catch(() => {
                // On error, close the dialog
                handleClose();
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const tabs = [
        {
            key: 'detail',
            caption: t('Detail'),
            component: (
                <BonoDetails
                    item={bono}
                    onSave={(savedBono) => {
                        setBono(savedBono as BonoData);
                        onChange?.(savedBono as BonoData);
                    }}
                    onDelete={(deletedBono) => {
                        onChange?.(deletedBono as BonoData);
                        handleClose();
                    }}
                />
            ),
        },
        {
            key: 'users',
            caption: t('Users'),
            component: bono && <BonoBoletos item={bono} />,
            show: bono?.id !== 'create',
        },
        {
            key: 'devices',
            caption: t('Devices'),
            component: bono && typeof bono.id === 'number' && (
                <BonoDevices
                    item={
                        bono as {
                            id: number;
                            name: string;
                            dateend: string;
                            timeend: string;
                        }
                    }
                />
            ),
            show: bono?.id !== 'create',
        },
    ];

    // In standalone page mode, always show as open (even while loading)
    const isOpen = isStandalonePage ? id !== null : id !== null && !!bono?.id;

    return (
        <SttFullDialog
            title={id === 'create' ? t('CreateBono') : bono?.name || ''}
            open={isOpen}
            onClose={handleClose}
            urlPath={
                disableUrlPath ||
                isStandalonePage ||
                id === 'create' ||
                !id ||
                trash
                    ? undefined
                    : `/bonos/${id}`
            }
        >
            {loading ? (
                <Box className="flex items-center justify-center py-20">
                    <CircularProgress />
                </Box>
            ) : (
                <SttTabs
                    tabs={tabs}
                    initialTab={initialTab}
                    onTabChange={handleTabChange}
                />
            )}
        </SttFullDialog>
    );
}
